/**
 * Centralized Logging Utility
 *
 * Provides environment-aware console logging:
 * - Production (www.dramadetector.com): Logs disabled (except errors for Sentry)
 * - Staging (st.dramadetector.com): Logs enabled
 * - Development (localhost): Logs enabled
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/logger';
 *
 * logger.log('[Component]', 'Initialized');
 * logger.warn('[Component]', 'Fallback mode');
 * logger.error('[Component]', 'Failed:', error); // Always sent to Sentry
 * ```
 */

import { isProduction } from './environment';

type LogLevel = 'log' | 'debug' | 'info' | 'warn' | 'error';

// Add telemetry callback interface
type BreadcrumbCallback = (breadcrumb: {
  category: string;
  message: string;
  data?: Record<string, unknown>;
  level: "debug" | "info" | "warn" | "error";
}) => void;

class Logger {
  private shouldLog: boolean;
  private breadcrumbCallback?: BreadcrumbCallback;

  constructor() {
    // Disable logs in production, enable everywhere else
    this.shouldLog = !isProduction();
  }

  /**
   * Configure telemetry (call once at app startup with Sentry)
   */
  configureTelemetry(callback: BreadcrumbCallback): void {
    this.breadcrumbCallback = callback;
  }

  /**
   * Add breadcrumb for telemetry (Sentry, etc.)
   */
  breadcrumb(category: string, message: string, data?: Record<string, unknown>, level: "debug" | "info" | "warn" | "error" = "info"): void {
    if (this.breadcrumbCallback) {
      this.breadcrumbCallback({ category, message, data, level });
    }
    // Also log locally in non-production
    if (this.shouldLog) {
      console.log(`[${category}] ${message}`, data || "");
    }
  }
  /**
   * Standard log (informational)
   * Disabled in production
   */
  log(...args: any[]): void {
    if (this.shouldLog) {
      console.log(...args);
    }
  }

  /**
   * Debug logs (verbose information)
   * Disabled in production
   */
  debug(...args: any[]): void {
    if (this.shouldLog) {
      console.debug(...args);
    }
  }

  /**
   * Info logs (important information)
   * Disabled in production
   */
  info(...args: any[]): void {
    if (this.shouldLog) {
      console.info(...args);
    }
  }

  /**
   * Warning logs (non-critical issues)
   * Disabled in production
   */
  warn(...args: any[]): void {
    if (this.shouldLog) {
      console.warn(...args);
    }
  }

  /**
   * Error logs (critical issues)
   * ALWAYS enabled (needed for Sentry error tracking)
   */
  error(...args: any[]): void {
    // Always log errors (Sentry needs them)
    console.error(...args);
  }

  /**
   * Force log (bypass production check)
   * Use sparingly for critical system messages
   */
  force(...args: any[]): void {
    console.log(...args);
  }

  /**
   * Conditional logging based on custom condition
   */
  conditional(condition: boolean, level: LogLevel, ...args: any[]): void {
    if (condition) {
      this[level](...args);
    }
  }

  /**
   * Get current logging state
   */
  isEnabled(): boolean {
    return this.shouldLog;
  }

  /**
   * Group logs (for collapsible sections)
   */
  group(label: string): void {
    if (this.shouldLog) {
      console.group(label);
    }
  }

  groupCollapsed(label: string): void {
    if (this.shouldLog) {
      console.groupCollapsed(label);
    }
  }

  groupEnd(): void {
    if (this.shouldLog) {
      console.groupEnd();
    }
  }

  /**
   * Table output (useful for arrays/objects)
   */
  table(data: any): void {
    if (this.shouldLog) {
      console.table(data);
    }
  }

  /**
   * Performance timing
   */
  time(label: string): void {
    if (this.shouldLog) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.shouldLog) {
      console.timeEnd(label);
    }
  }
}

// Singleton instance
export const logger = new Logger();

/**
 * Legacy compatibility: Export individual functions
 * (allows gradual migration from console.* to logger.*)
 */
export const { log, debug, info, warn, error } = logger;
